// Static stand-ins for the real API, used when VITE_MOCK_DATA=true so the
// map/filters/admin UI are exercisable with no AWS account, Cognito login,
// or network access at all.
import { ApiError } from './errors';
import type {
  AdminSummary,
  ApiKeySummary,
  ArchiveUserResult,
  CheckinItem,
  CheckinsResponse,
  CreatedApiKey,
  CreatedUser,
  CurrentAdminIdentity,
  DeleteDeviceResult,
  DeviceSummary,
  OverviewResponse,
  RegistrationToken,
  RenameDeviceResult,
  RevokeDeviceResult,
  TrailResponse,
} from './types';

function minutesAgo(m: number): string {
  return new Date(Date.now() - m * 60_000).toISOString();
}

const mockOverview: OverviewResponse = {
  users: [
    {
      user_id: 'u-adam',
      display_name: 'Adam',
      devices: [
        {
          device_id: 'd-adam-tablet',
          device_name: "Adam's tablet",
          model: 'Lenovo TB-X606F',
          manufacturer: 'LENOVO',
          status: 'active',
          last_seen_at: minutesAgo(4),
          last_lat: 40.7128,
          last_lng: -74.006,
          last_battery: 81,
          last_accuracy: 12,
          last_imprecise: false,
        },
      ],
    },
    {
      user_id: 'u-mia',
      display_name: 'Mia',
      devices: [
        {
          device_id: 'd-mia-phone',
          device_name: "Mia's phone",
          model: 'Pixel 8a',
          manufacturer: 'Google',
          status: 'active',
          last_seen_at: minutesAgo(35),
          last_lat: 40.72,
          last_lng: -74.0,
          last_battery: 54,
          last_accuracy: 18,
          last_imprecise: false,
        },
      ],
    },
    {
      user_id: 'u-sam',
      display_name: 'Sam',
      devices: [
        {
          device_id: 'd-sam-phone',
          device_name: "Sam's phone",
          model: 'Galaxy A15',
          manufacturer: 'Samsung',
          status: 'active',
          last_seen_at: minutesAgo(95),
          last_lat: 40.705,
          last_lng: -74.012,
          last_battery: 12,
          last_accuracy: 250,
          last_imprecise: true,
        },
        {
          device_id: 'd-sam-old-phone',
          device_name: 'Old phone (spare)',
          model: 'Galaxy A03',
          manufacturer: 'Samsung',
          status: 'revoked',
          last_seen_at: minutesAgo(60 * 24 * 10),
          last_lat: 40.71,
          last_lng: -74.02,
          last_battery: 0,
          last_accuracy: 30,
          last_imprecise: false,
        },
      ],
    },
  ],
};

// The "you" of the mock session - matches the first entry below so the
// Admins section can demo hiding the Remove button on your own row.
const MOCK_CURRENT_ADMIN: CurrentAdminIdentity = { username: 'asieting', email: 'asieting@sieting.family' };

const mockAdmins: AdminSummary[] = [
  { username: 'asieting', email: 'asieting@sieting.family', name: 'Adam Sieting', status: 'CONFIRMED', enabled: true },
  { username: 'newadmin', email: 'newadmin@example.com', name: 'Pending Admin', status: 'FORCE_CHANGE_PASSWORD', enabled: true },
];

const mockApiKeys: ApiKeySummary[] = [
  {
    key_id: 'k-claude-mcp',
    label: 'Claude MCP',
    prefix: 'fltk_a1b2c3',
    status: 'active',
    created_at: minutesAgo(60 * 24 * 3),
    last_used_at: minutesAgo(20),
  },
];

function findDevice(deviceId: string): DeviceSummary | undefined {
  return mockOverview.users.flatMap((u) => u.devices).find((d) => d.device_id === deviceId);
}

function mockTrail(deviceId: string, hours: number): TrailResponse {
  const base = findDevice(deviceId);
  const lat = base?.last_lat ?? 40.71;
  const lng = base?.last_lng ?? -74.0;
  const points = Array.from({ length: 12 }, (_, i) => {
    const minutesBack = ((11 - i) * (hours * 60)) / 12;
    return {
      lat: lat + (Math.random() - 0.5) * 0.01,
      lng: lng + (Math.random() - 0.5) * 0.01,
      timestamp: minutesAgo(minutesBack),
      battery: Math.max(0, (base?.last_battery ?? 50) + i),
      accuracy: i === 2 ? 300 : 15,
      imprecise: i === 2,
    };
  });
  return { device_id: deviceId, hours, points };
}

function mockCheckins(deviceId: string, hours: number): CheckinsResponse {
  const base = findDevice(deviceId);
  const lat = base?.last_lat ?? 40.71;
  const lng = base?.last_lng ?? -74.0;
  const count = 8;
  const checkins: CheckinItem[] = Array.from({ length: count }, (_, i) => {
    const minutesBack = (i * hours * 60) / count;
    const timestamp = minutesAgo(minutesBack);
    const receivedAt = new Date(new Date(timestamp).getTime() + (i === 1 ? 4 * 60_000 : 8_000)).toISOString();
    const imprecise = i === 1;
    const item: CheckinItem = {
      device_id: deviceId,
      timestamp,
      received_at: receivedAt,
      lat: lat + (Math.random() - 0.5) * 0.01,
      lng: lng + (Math.random() - 0.5) * 0.01,
      accuracy: imprecise ? 275 : 14,
      battery: Math.max(0, (base?.last_battery ?? 50) - i),
      imprecise,
    };
    // Mirror the real device payload: the checkins endpoint returns the raw
    // upload verbatim, so connected_ssid (v2, may be entirely absent) and
    // wifi.ssid (v1, always present) are two independent sources - see
    // connectedSsid() in pages/deviceCheckins.ts.
    if (i === 0 || i === 3) {
      item.connected_ssid = 'HomeWiFi-5G';
    } else if (i === 2) {
      // v2 field never sent this ping; only the v1 fallback identifies the network.
      item.wifi = { connected: true, ssid: 'CoffeeShop_Guest', bssid: 'aa:bb:cc:dd:ee:ff', signal: -58, hidden: false };
    } else if (i === 5) {
      item.wifi = { connected: false, ssid: '', bssid: '', signal: 0, hidden: false };
    }
    return item;
  });
  return { device_id: deviceId, hours, checkins };
}

async function delay<T>(value: T): Promise<T> {
  await new Promise((r) => setTimeout(r, 150));
  return value;
}

export const mockApi = {
  overview: (): Promise<OverviewResponse> => delay(structuredClone(mockOverview)),

  trail: (deviceId: string, hours: number): Promise<TrailResponse> =>
    delay(mockTrail(deviceId, hours)),

  checkins: (deviceId: string, hours: number): Promise<CheckinsResponse> =>
    delay(mockCheckins(deviceId, hours)),

  createUser: (displayName: string): Promise<CreatedUser> => {
    const user_id = `u-${Math.random().toString(36).slice(2, 8)}`;
    mockOverview.users.push({ user_id, display_name: displayName, devices: [] });
    return delay({
      user_id,
      display_name: displayName,
      created_at: new Date().toISOString(),
      status: 'active',
    });
  },

  archiveUser: (userId: string): Promise<ArchiveUserResult> => {
    const user = mockOverview.users.find((u) => u.user_id === userId);
    const revoked = user?.devices.map((d) => d.device_id) ?? [];
    user?.devices.forEach((d) => {
      d.status = 'revoked';
    });
    return delay({ user_id: userId, status: 'archived', revoked_devices: revoked });
  },

  mintRegistrationToken: (_userId: string): Promise<RegistrationToken> =>
    delay({
      token: `mock-token-${Math.random().toString(36).slice(2)}`,
      short_code: 'MOCK1234',
      expires_at: Math.floor(Date.now() / 1000) + 15 * 60,
    }),

  revokeDevice: (deviceId: string): Promise<RevokeDeviceResult> => {
    const device = findDevice(deviceId);
    if (device) device.status = 'revoked';
    return delay({ device_id: deviceId, status: 'revoked' });
  },

  renameDevice: (deviceId: string, deviceName: string): Promise<RenameDeviceResult> => {
    const device = findDevice(deviceId);
    if (device) device.device_name = deviceName;
    return delay({ device_id: deviceId, device_name: deviceName });
  },

  deleteDevice: (deviceId: string): Promise<DeleteDeviceResult> => {
    for (const user of mockOverview.users) {
      const idx = user.devices.findIndex((d) => d.device_id === deviceId);
      if (idx !== -1) user.devices.splice(idx, 1);
    }
    return delay({ device_id: deviceId, status: 'deleted' });
  },

  listAdmins: (): Promise<AdminSummary[]> => delay(structuredClone(mockAdmins)),

  inviteAdmin: (email: string, name: string): Promise<AdminSummary> => {
    if (mockAdmins.some((a) => a.email === email)) {
      return Promise.reject(new ApiError(409, `An admin with email "${email}" already exists.`));
    }
    const created: AdminSummary = {
      username: email.split('@')[0] || email,
      email,
      name,
      status: 'FORCE_CHANGE_PASSWORD',
      enabled: true,
    };
    mockAdmins.push(created);
    return delay(created);
  },

  removeAdmin: (username: string): Promise<void> => {
    if (username === MOCK_CURRENT_ADMIN.username) {
      return Promise.reject(new ApiError(403, 'You cannot remove your own admin account.'));
    }
    const idx = mockAdmins.findIndex((a) => a.username === username);
    if (idx !== -1) mockAdmins.splice(idx, 1);
    return delay(undefined);
  },

  currentAdminIdentity: (): CurrentAdminIdentity => MOCK_CURRENT_ADMIN,

  listApiKeys: (): Promise<ApiKeySummary[]> => delay(structuredClone(mockApiKeys)),

  createApiKey: (label: string): Promise<CreatedApiKey> => {
    const key_id = `k-${Math.random().toString(36).slice(2, 10)}`;
    const secret = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    const key = `fltk_${secret}`;
    const prefix = key.slice(0, 11);
    const created_at = new Date().toISOString();
    mockApiKeys.push({ key_id, label, prefix, status: 'active', created_at, last_used_at: null });
    return delay({ key_id, label, prefix, key, created_at });
  },

  revokeApiKey: (keyId: string): Promise<void> => {
    const key = mockApiKeys.find((k) => k.key_id === keyId);
    if (!key) {
      return Promise.reject(new ApiError(404, `Unknown API key "${keyId}".`));
    }
    key.status = 'revoked';
    return delay(undefined);
  },
};
